import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TabsContent } from '../../components/ui/tabs';
import { Share2, X, AlertCircle } from 'lucide-react';

export const ShareSection = ({
  // Tab value (e.g., 'sf-share', 'cw-share', 'share')
  tabValue,
  // Input ID prefix for unique IDs
  inputIdPrefix,
  // Data
  csiItem,
  project,
  user,
  shareCompanySearch,
  showCompanySuggestions,
  companySuggestions,
  highlightedIndex,
  searchingCompanies,
  // Handlers
  handleSearchCompanies,
  handleCompanySearchKeyDown,
  setShowCompanySuggestions,
  setHighlightedIndex,
  handleSelectCompany,
  handleUnshareCSI,
  // Optional: render without TabsContent wrapper (for modal usage)
  asContent = false,
  // Optional: render without Card/Header frame (for clean modal usage)
  asModal = false,
}) => {
  const isOwnerOrSameCompany = project?.createdBy === user?.id || 
    (project?.companyId && user?.companyId && project.companyId === user.companyId);

  const innerContent = (
    <div className="space-y-6">
        {/* Current Share Status */}
        {csiItem.assignedCompanyId ? (
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Currently shared with:</p>
                <p className="text-lg font-semibold text-slate-900">{csiItem.assignedCompanyName || 'Unknown Company'}</p>
                {csiItem.assignedCompanyAddress && (
                  <p className="text-sm text-slate-500 mt-1">{csiItem.assignedCompanyAddress}</p>
                )}
              </div>
              {isOwnerOrSameCompany && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleUnshareCSI(csiItem)}
                >
                  <X className="h-4 w-4 mr-1" />
                  Unshare
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <p className="text-slate-600">This specification is not currently shared with any sub-contractor.</p>
          </div>
        )}

        {/* Share Form - For owner or same company members */}
        {isOwnerOrSameCompany && !csiItem.assignedCompanyId && (
          <div className="space-y-4 border-t pt-6">
            <div>
              <h3 className="text-lg font-semibold mb-1">Select Sub-Contractor</h3>
              <p className="text-sm text-slate-600">Choose a company to share this specification with</p>
            </div>
            
            <div className="space-y-2 relative">
              <Label htmlFor={`${inputIdPrefix}-company-search`}>Company Name</Label>
              <Input
                id={`${inputIdPrefix}-company-search`}
                type="text"
                placeholder="Start typing company name..."
                value={shareCompanySearch}
                onChange={(e) => handleSearchCompanies(e.target.value)}
                onKeyDown={(e) => handleCompanySearchKeyDown(e, csiItem)}
                onFocus={() => {
                  if (shareCompanySearch.length >= 2) {
                    setShowCompanySuggestions(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowCompanySuggestions(false);
                    setHighlightedIndex(-1);
                  }, 200);
                }}
                autoComplete="off"
              />
              
              {showCompanySuggestions && companySuggestions.length > 0 && (
                <div className="company-suggestions-dropdown absolute z-50 w-full mt-1 border rounded-md shadow-lg bg-white max-h-48 overflow-y-auto">
                  {companySuggestions.map((company, index) => (
                    <div
                      key={company.id}
                      onClick={() => handleSelectCompany(company, csiItem)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`px-4 py-3 cursor-pointer transition-colors ${
                        index === highlightedIndex 
                          ? 'bg-orange-50 border-l-4 border-orange-500' 
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{company.companyName || company.name}</p>
                          <p className="text-sm text-slate-600">{company.companyType}</p>
                        </div>
                        {index === highlightedIndex && (
                          <span className="text-xs text-orange-600 font-medium">Press Enter</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {searchingCompanies && (
                <p className="text-sm text-slate-500 mt-2">
                  <span className="inline-block animate-pulse">Searching...</span>
                </p>
              )}
              
              {shareCompanySearch.length > 0 && shareCompanySearch.length < 2 && (
                <p className="text-sm text-slate-500 mt-2">Type at least 2 characters to search</p>
              )}
            </div>
          </div>
        )}

        {/* Access Denied Message */}
        {!isOwnerOrSameCompany && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
            <p className="text-amber-800">
              <AlertCircle className="inline w-4 h-4 mr-1" />
              Only the project owner or team members from the same company can manage sharing settings.
            </p>
          </div>
        )}
    </div>
  );

  if (asModal) return innerContent;

  const content = (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5" />
          Share This Specification
        </CardTitle>
        <CardDescription>
          Share {csiItem.itemTitle} specification with a sub-contractor company
        </CardDescription>
      </CardHeader>
      <CardContent>
        {innerContent}
      </CardContent>
    </Card>
  );

  if (asContent) return content;

  return (
    <TabsContent value={tabValue}>
      {content}
    </TabsContent>
  );
};

export default ShareSection;
